import { useEffect, useRef, useState } from 'react';

export default function ElectricCursor() {
  const cursorRef = useRef(null);
  const mousePos = useRef({ x: -200, y: -200 });
  const cursorPos = useRef({ x: -200, y: -200 });
  const rafId = useRef(null);
  const visibleRef = useRef(false);
  const [visible, setVisible] = useState(false);
  const canvasRef = useRef(null);

  useEffect(() => {
    visibleRef.current = visible;
  }, [visible]);

  useEffect(() => {
    const lerp = (a, b, t) => a + (b - a) * t;

    const animate = () => {
      cursorPos.current.x = lerp(cursorPos.current.x, mousePos.current.x, 0.12);
      cursorPos.current.y = lerp(cursorPos.current.y, mousePos.current.y, 0.12);

      if (cursorRef.current) {
        cursorRef.current.style.transform = `translate(${cursorPos.current.x}px, ${cursorPos.current.y}px)`;
      }

      if (canvasRef.current) {
        const ctx = canvasRef.current.getContext('2d');
        const w = canvasRef.current.width;
        const h = canvasRef.current.height;
        const cx = w / 2;
        const cy = h / 2;
        const radius = 46;

        ctx.clearRect(0, 0, w, h);
        const time = Date.now() * 0.003;
        const arcCount = 8;

        for (let i = 0; i < arcCount; i++) {
          const angle = (i / arcCount) * Math.PI * 2 + time;
          const nextAngle = angle + (Math.PI * 2) / arcCount;
          const intensity = 0.3 + Math.random() * 0.7;

          ctx.beginPath();
          ctx.strokeStyle = `rgba(59, 130, 246, ${intensity * 0.8})`;
          ctx.lineWidth = 1 + Math.random() * 1.5;
          ctx.shadowColor = '#3B82F6';
          ctx.shadowBlur = 8 + Math.random() * 8;

          const segments = 6 + Math.floor(Math.random() * 4);
          for (let s = 0; s <= segments; s++) {
            const t = s / segments;
            const a = angle + (nextAngle - angle) * t;
            const jitter = (Math.random() - 0.5) * 8;
            const r = radius + jitter;
            const px = cx + Math.cos(a) * r;
            const py = cy + Math.sin(a) * r;
            if (s === 0) ctx.moveTo(px, py);
            else ctx.lineTo(px, py);
          }
          ctx.stroke();
        }

        if (Math.random() > 0.6) {
          const sparkAngle = Math.random() * Math.PI * 2;
          const sparkR = radius + (Math.random() - 0.5) * 12;
          ctx.beginPath();
          ctx.arc(cx + Math.cos(sparkAngle) * sparkR, cy + Math.sin(sparkAngle) * sparkR, 1 + Math.random() * 2, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(147, 197, 253, ${0.6 + Math.random() * 0.4})`;
          ctx.shadowColor = '#93C5FD';
          ctx.shadowBlur = 12;
          ctx.fill();
        }
        ctx.shadowBlur = 0;
      }

      rafId.current = requestAnimationFrame(animate);
    };

    const handleMouseMove = (e) => {
      mousePos.current = { x: e.clientX, y: e.clientY };
      if (!visibleRef.current) setVisible(true);
    };
    const handleMouseLeave = () => setVisible(false);
    const handleMouseEnter = () => setVisible(true);

    window.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('mouseenter', handleMouseEnter);
    rafId.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('mouseenter', handleMouseEnter);
      if (rafId.current) cancelAnimationFrame(rafId.current);
    };
  }, []);

  return (
    <>
      <style>{`
        .electric-cursor-page,
        .electric-cursor-page * {
          cursor: none !important;
        }
        .electric-cursor-page a,
        .electric-cursor-page button,
        .electric-cursor-page input,
        .electric-cursor-page textarea,
        .electric-cursor-page select {
          cursor: none !important;
        }
      `}</style>
      <div
        ref={cursorRef}
        data-testid="electric-cursor"
        className="fixed pointer-events-none z-[10000]"
        style={{ left: -60, top: -60, width: 120, height: 120, opacity: visible ? 1 : 0, transition: 'opacity 0.3s ease' }}
      >
        <canvas ref={canvasRef} width={120} height={120} className="absolute inset-0 w-full h-full" />
        <div className="absolute inset-0 rounded-full" style={{ border: '1.5px dashed rgba(59, 130, 246, 0.4)', animation: 'electric-spin 4s linear infinite' }} />
        <div className="absolute rounded-full" style={{ inset: '6px', border: '1px dashed rgba(96, 165, 250, 0.25)', animation: 'electric-spin-reverse 3s linear infinite' }} />
        <div className="absolute rounded-full" style={{ inset: '3px', boxShadow: '0 0 15px rgba(59, 130, 246, 0.15), inset 0 0 15px rgba(59, 130, 246, 0.08)', animation: 'electric-pulse 2s ease-in-out infinite' }} />
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-white font-bold select-none whitespace-nowrap" style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontSize: '10px', letterSpacing: '0.5px', textShadow: '0 0 8px rgba(59, 130, 246, 0.5), 0 0 20px rgba(59, 130, 246, 0.2)' }}>
            {'< '}<span style={{ color: '#60A5FA' }}>Astra</span><span style={{ color: '#FFFFFF' }}>Volt</span>{' >'}
          </span>
        </div>
      </div>
    </>
  );
}
